import {hotmathsContent} from './hotmaths';

/**
 * Cambridge HOTmaths India (www.hotmaths.in) homepage copy.
 *
 * Carried across from the Struts page `src/main/webapp/home/india/index.jsp`, in the same spirit as `hotmaths.js`:
 * order and hierarchy change, words do not.
 *
 * Where the brand differs from HOTmaths:
 * - Schools only, at a per-student rate written on the page, so pricing is `pricing.schoolOnly` with a Register
 *   action rather than live product prices.
 * - Registration and password reset are the React pages (`/home/register`, `/home/reset`); terms and privacy links
 *   there come from india.json.
 * - Six sample lessons, one per class from 3 to 8.
 * - The privacy policy and terms were full-screen modals; they are React pages on the same bundle now (see
 *   `routes`), reading the same fragments the modals include.
 *
 * Not carried across: the contact form (the Struts `submitContactUs` action; Help in the header is the route to
 * support now), the "Read more about us" and "About the writers" modals (Australian copy), and the System
 * Requirements modal.
 */
const REGISTER_URL = '/home/register';

export const indiaContent = {
    productName: 'Cambridge HOTmaths',

    links: {
        home: '/',
        register: REGISTER_URL,
    },

    intro: {
        heading: 'Cambridge HOTmaths',
        // The opening of index.jsp's About paragraph.
        standfirst: 'A comprehensive mathematics learning system for classes 3 to 8 - an interactive online maths '
            + 'learning, teaching and assessment resource for students and teachers, for individuals or whole '
            + 'classes, for school and at home.',
        primaryAction: {label: 'Get a 14-day trial', href: '#ch-home-trial'},
        secondaryAction: {label: 'View sample lessons', href: '#ch-home-lessons'},
    },

    /** The lesson contents listed under index.jsp's "School pricing". */
    features: {
        heading: 'What you get',
        items: [
            {label: 'Lessons', icon: 'lessons'},
            {label: 'Interactive and printable resources', icon: 'resources'},
            {label: 'Walkthroughs', icon: 'walkthrough'},
            {label: 'Questions', icon: 'questions'},
            {label: 'Scorcher', icon: 'scorcher'},
            {label: 'Dictionary', icon: 'dictionary'},
        ],
    },

    /** From index.jsp's "Try Cambridge HOTmaths" section. */
    trial: {
        sectionHeading: 'Try Cambridge HOTmaths',
        heading: 'Get a 14-day trial',
        body: 'Explore professionally designed teaching and learning tools including lessons, walkthroughs, '
            + 'questions, animated demonstrations, Scorcher, worksheets and solutions, working mathematically '
            + 'investigations, the maths dictionary and our online maths competition.',
        teacherNote: 'Teachers, see the full benefits of HOTmaths. We can also organise a class trial for you and '
            + 'your students.',
    },

    /** From index.jsp's "School pricing". The rate is copy there too; there is no product behind it. */
    pricing: {
        heading: 'School pricing',
        schoolOnly: true,
        schoolPrice: 'INR 1499',
        schoolNote: 'per student per academic year',
        body: 'A Cambridge HOTmaths subscription provides you with access to all resources for the selected level '
            + 'including the learning management system.',
        footnote: 'Each topic includes topic quizzes and lessons, and a set of printed worksheets.',
        action: {label: 'Register', href: REGISTER_URL},
    },

    /** The same video as HOTmaths (index.jsp plays the same files), without the Australian gallery and webinars. */
    video: {
        heading: hotmathsContent.video.heading,
        poster: hotmathsContent.video.poster,
        sources: hotmathsContent.video.sources,
    },

    about: {
        heading: 'About Cambridge HOTmaths',
        paragraphs: [
            'Cambridge HOTmaths is a comprehensive mathematics learning system for classes 3 to 8 - an interactive '
                + 'online maths learning, teaching and assessment resource for students and teachers, for '
                + 'individuals or whole classes, for school and at home, for all curricula, programmes and '
                + 'qualifications - CBSE, ICSE, State Boards or International.',
            'A suite of flexible resources (printed, printable and online) are provided for all types of '
                + 'classrooms ranging from those with no technology to full laptop access, and includes projectable '
                + 'material in all lessons for interactive and non-interactive whiteboards.',
            'Developed by a dedicated group of educators, technicians and designers, Cambridge HOTmaths is a high '
                + 'quality maths resource for students and teachers.',
        ],
    },

    otherProducts: {
        heading: 'Other Cambridge products',
        // index.jsp's quotes were unbalanced ("I Did It'Mathematics"); the titles are "I Did It" Mathematics
        // Revised Edition and Maths Mileage.
        body: 'Are you looking for "I Did It" Mathematics Revised Edition or "Maths Mileage"?',
        link: {label: 'cambridge.org/education', href: 'https://www.cambridge.org/education'},
    },

    sampleLessons: {
        heading: 'Sample lessons',
        intro: 'Access widgets, walkthroughs, questions and Scorcher in each sample lesson listed below.',
        note: 'Some functionality is available only to subscribers.',
        lessons: [
            {level: 'Class 3', topic: 'Fractions', title: 'Fractional parts', syllabusId: 579},
            {level: 'Class 4', topic: 'Decimals & money', title: 'Decimals for 10ths, 100ths & 1000ths', syllabusId: 580},
            {level: 'Class 5', topic: 'Angles & lines', title: 'The language of angles & lines', syllabusId: 581},
            {level: 'Class 6', topic: 'Ratio, proportion & unitary method', title: 'Ratio & proportion', syllabusId: 582},
            {level: 'Class 7', topic: 'Constructions & symmetry', title: 'Constructing triangles & parallel lines', syllabusId: 583},
            {level: 'Class 8', topic: 'The Cartesian plane', title: 'The first quadrant', syllabusId: 584},
        ],
    },

    /**
     * The brand's other public pages, rendered by the homepage bundle and matched with wouter (index.js). The
     * server serves these paths directly (WEB-INF/urlrewrite.xml).
     */
    routes: [
        {path: '/home/india/terms', page: 'legal', title: 'Terms and Conditions',
            fragment: '/home/india/fragments/terms.html'},
        {path: '/home/india/privacy', page: 'legal', title: 'Privacy Policy',
            fragment: '/home/india/fragments/privacy.html'},
    ],

    /** From the old footer (footerLinks.jsp), which opened these as modals. */
    footerLinks: [
        {label: 'Privacy Policy', href: '/home/india/privacy'},
        {label: 'Terms and Conditions', href: '/home/india/terms'},
    ],

    nav: [
        // Sections of the homepage, written as "/#..." so they also work from the legal pages.
        {label: 'About', href: '/#ch-home-about'},
        {label: 'Sample lessons', href: '/#ch-home-lessons'},
        // The old menu's "Support" (loadHelp()).
        {label: 'Help', href: '/usersCommon/showHelp.action'},
    ],
};

export default indiaContent;
